import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Shield, CreditCard } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Seed data first
      await axios.post(`${API}/seed`).catch(() => {});
      
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products?limit=8`),
        axios.get(`${API}/categories`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Truck, titleKey: 'fastDelivery', descKey: 'deliveryDesc' },
    { icon: Shield, titleKey: 'security', descKey: 'securityDesc' },
    { icon: CreditCard, titleKey: 'easyPayment', descKey: 'paymentDesc' },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[600px] bg-mrc-navy overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/5632397/pexels-photo-5632397.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
            alt="Shopping" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            {/* Welcome Banner */}
            <div className="bg-mrc-orange text-white px-6 py-2 inline-block mb-6">
              <span className="font-heading text-lg italic">{t('welcome')}</span>
            </div>
            
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white leading-tight mb-6">
              {t('heroTitle')}
            </h1>
            <p className="text-xl md:text-2xl text-mrc-orange font-body italic mb-8">
              {t('tagline')}
            </p>
            <p className="text-lg text-gray-300 mb-8 max-w-xl">
              {t('heroDesc')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/products">
                <Button 
                  className="bg-mrc-orange hover:bg-mrc-orange/90 text-white px-8 py-6 text-lg font-semibold uppercase tracking-wider rounded-full"
                  data-testid="shop-now-btn"
                >
                  {t('shopNow')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/register">
                <Button 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-mrc-navy px-8 py-6 text-lg font-semibold uppercase tracking-wider rounded-full"
                  data-testid="signup-hero-btn"
                >
                  {t('register')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white py-12 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={feature.titleKey}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="flex items-center gap-4 p-4"
              >
                <div className="bg-mrc-navy/10 p-4 rounded-full">
                  <feature.icon className="h-6 w-6 text-mrc-navy" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-mrc-navy">{t(feature.titleKey)}</h3>
                  <p className="text-muted-foreground text-sm">{t(feature.descKey)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-mrc-navy mb-4">
              {t('categories')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('exploreCategories')}
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Link 
                  to={`/products?category=${category.name}`}
                  className="group relative block aspect-square overflow-hidden"
                  data-testid={`category-${category.name}`}
                >
                  <img 
                    src={category.image_url} 
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 category-overlay" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="font-heading text-xl font-bold text-white">{category.name}</h3>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-mrc-navy mb-2">
                {t('popularProducts')}
              </h2>
              <p className="text-muted-foreground">
                {t('discoverPopular')}
              </p>
            </div>
            <Link to="/products" className="hidden md:flex items-center gap-2 text-mrc-navy font-semibold hover:text-mrc-orange transition-colors">
              {t('viewAll')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 aspect-square mb-4" />
                  <div className="bg-gray-200 h-4 w-20 mb-2" />
                  <div className="bg-gray-200 h-6 w-32 mb-2" />
                  <div className="bg-gray-200 h-5 w-16" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="products-grid">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          )}

          <div className="mt-8 text-center md:hidden">
            <Link to="/products">
              <Button className="bg-mrc-navy hover:bg-mrc-navy/90 text-white">
                {t('viewAll')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-mrc-orange py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            {t('dontMissPromotions')}
          </h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">
            {t('signUpForNews')}
          </p>
          <Link to="/register">
            <Button className="bg-mrc-navy hover:bg-mrc-navy/90 text-white px-8 py-6 text-lg font-semibold uppercase tracking-wider rounded-full">
              {t('createFreeAccount')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
